import React, { useEffect, useState } from 'react';
import { db, auth } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, doc, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import toast from 'react-hot-toast';

export const SupportPage = () => {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Real-time listener for support tickets
    const q = query(collection(db, 'support_tickets'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const ticketsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTickets(ticketsData);
      setLoading(false);
    }, (err) => {
      console.error(err);
      toast.error('Failed to load tickets');
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const updateTicketStatus = async (ticketId, newStatus) => {
    try {
      await updateDoc(doc(db, 'support_tickets', ticketId), { status: newStatus });
      toast.success(`Ticket status updated to ${newStatus}`);
      if (selectedTicket && selectedTicket.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, status: newStatus });
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    if (!selectedTicket) return;
    try {
      const newMessage = {
        text: replyText,
        sentBy: auth.currentUser?.email || 'admin',
        sentAt: new Date(),
        isAdmin: true,
      };
      const messages = selectedTicket.messages || [];
      await updateDoc(doc(db, 'support_tickets', selectedTicket.id), {
        messages: [...messages, newMessage],
        lastUpdated: new Date(),
        status: 'in_progress' // change status when admin replies
      });
      setReplyText('');
      toast.success('Reply sent');
      // Refresh selected ticket (will be updated by real-time listener)
    } catch (err) {
      toast.error('Failed to send reply');
    }
  };

  if (loading) return <div className="flex justify-center p-8"><div className="text-gray-400">Loading support tickets...</div></div>;

  return (
    <div className="flex h-full gap-6">
      {/* Tickets List (left column) */}
      <div className="w-1/3 bg-gray-800 rounded-lg p-4 overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">Support Tickets</h2>
        {tickets.length === 0 ? <p className="text-gray-400">No tickets yet.</p> : (
          <div className="space-y-2">
            {tickets.map(ticket => (
              <div
                key={ticket.id}
                onClick={() => setSelectedTicket(ticket)}
                className={`p-3 rounded-lg cursor-pointer transition ${selectedTicket?.id === ticket.id ? 'bg-gray-700' : 'hover:bg-gray-700'}`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">{ticket.subject}</p>
                    <p className="text-xs text-gray-400">User: {ticket.userEmail || ticket.userId?.slice(-6)}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    ticket.status === 'open' ? 'bg-red-900 text-red-300' :
                    ticket.status === 'in_progress' ? 'bg-yellow-900 text-yellow-300' : 'bg-green-900 text-green-300'
                  }`}>
                    {ticket.status || 'open'}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-1">{ticket.createdAt?.toDate().toLocaleString()}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Ticket Detail & Reply (right column) */}
      <div className="flex-1 bg-gray-800 rounded-lg p-4 flex flex-col">
        {selectedTicket ? (
          <>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-semibold">{selectedTicket.subject}</h3>
                <p className="text-sm text-gray-400">
                  From: {selectedTicket.userEmail || selectedTicket.userId} | 
                  Priority: {selectedTicket.priority || 'Normal'} |
                  Status: 
                  <select
                    value={selectedTicket.status || 'open'}
                    onChange={(e) => updateTicketStatus(selectedTicket.id, e.target.value)}
                    className="ml-2 bg-gray-700 text-white rounded px-2 py-0.5 text-sm"
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </p>
              </div>
              <button
                onClick={() => setSelectedTicket(null)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-y-auto mb-4 space-y-3">
              <div className="bg-gray-700 p-3 rounded-lg">
                <p className="text-sm"><strong>{selectedTicket.userEmail || 'User'}</strong> wrote:</p>
                <p className="mt-1">{selectedTicket.description}</p>
                <p className="text-xs text-gray-400 mt-1">{selectedTicket.createdAt?.toDate().toLocaleString()}</p>
              </div>
              {(selectedTicket.messages || []).map((msg, idx) => (
                <div key={idx} className={`p-3 rounded-lg ${msg.isAdmin ? 'bg-emerald-900 ml-8' : 'bg-gray-700 mr-8'}`}>
                  <p className="text-sm"><strong>{msg.sentBy}</strong> {msg.isAdmin ? '(Admin)' : ''}</p>
                  <p className="mt-1">{msg.text}</p>
                  <p className="text-xs text-gray-400 mt-1">{msg.sentAt?.toDate().toLocaleString()}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <textarea
                className="flex-1 bg-gray-700 border border-gray-600 rounded-lg p-2 text-sm"
                rows="3"
                placeholder="Type your reply..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
              />
              <button
                onClick={sendReply}
                className="bg-emerald-600 hover:bg-emerald-500 px-4 py-2 rounded-lg self-end"
              >
                Send Reply
              </button>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400">
            Select a ticket to view details and reply.
          </div>
        )}
      </div>
    </div>
  );
};
